import { memo, useMemo, useState } from "react";

import { SearchResult } from "./SourceListItem";
import { SideBarScroll } from "../ui/SideBarScroll";
import SourceCardsList from "./SourceCardsList";
import SourcesChunkView from "./SourcesChunkView";

type ViewMode = "dataChunk" | "cardList";

type SourceViewProps = {
  documentHeight: number;
  responseSources: SearchResult[];
  detailSourceViewSdrId: number | null;
  detailSourceViewSourceId: number | null;
  setDetailSourceViewSdrId: React.Dispatch<React.SetStateAction<number | null>>;
  setDetailSourceViewSourceId: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  viewMode?: ViewMode;
  setViewMode?: React.Dispatch<React.SetStateAction<ViewMode>>;
};

const SourceView = memo(
  ({
    documentHeight,
    responseSources,
    detailSourceViewSdrId,
    detailSourceViewSourceId,
    setDetailSourceViewSdrId,
    setDetailSourceViewSourceId,
    viewMode: externalViewMode,
  }: SourceViewProps) => {
    const [internalViewMode] = useState<ViewMode>("dataChunk");

    // ✅ Memoize view mode calculation
    const viewMode = useMemo(() => {
      return externalViewMode ?? internalViewMode;
    }, [externalViewMode, internalViewMode]);

    // ✅ Memoize props for SourceCardsList to prevent unnecessary rerenders
    const sourceCardsListProps = useMemo(
      () => ({
        responseSources,
        detailSourceViewSdrId,
        detailSourceViewSourceId,
        setDetailSourceViewSdrId,
        setDetailSourceViewSourceId,
        documentHeight,
      }),
      [
        responseSources.length,
        detailSourceViewSdrId,
        detailSourceViewSourceId,
        documentHeight,
      ],
    );

    return (
      <div
        data-testid="aa-src-panel"
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {viewMode === "dataChunk" && (
          <SideBarScroll>
            <SourcesChunkView sources={responseSources} />
          </SideBarScroll>
        )}

        {viewMode === "cardList" && (
          <SourceCardsList {...sourceCardsListProps} />
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Check if the sources array reference changed (proper React pattern)
    // This will re-render when responseSources is a new array
    const sourcesChanged =
      prevProps.responseSources !== nextProps.responseSources;

    return (
      !sourcesChanged &&
      prevProps.detailSourceViewSdrId === nextProps.detailSourceViewSdrId &&
      prevProps.detailSourceViewSourceId ===
        nextProps.detailSourceViewSourceId &&
      prevProps.viewMode === nextProps.viewMode &&
      prevProps.documentHeight === nextProps.documentHeight
    );
  },
);

export default SourceView;
